import { TenantColorsData, TenantOriginalsColorsData } from "../types/api"

/**
 * Mirror of the palette derivation in `@cryptocasino1/originals`
 * (`src/styles/originals.css`, OVERRIDE INPUTS block).
 *
 * Pre-fills the override form when the operator enables it, so that enabling
 * is a visual no-op and no picker default gets persisted by accident. Only the
 * formulas the form exposes are mirrored; the rest of the package's ladder
 * derives from these.
 *
 * Must stay in sync with the CSS by hand: each formula below carries the CSS
 * line it mirrors, the arithmetic is plain sRGB (identical to
 * `color-mix(in srgb, …)`), and OriginalsColorFields.test pins every mirrored
 * value for one brand.
 */

type Rgb = { r: number; g: number; b: number }

/** `#rgb` / `#rrggbb` → channels. Returns null for anything else (named colours, rgb(), empty). */
function parseHex(value: string | undefined): Rgb | null {
  if (!value) return null
  const hex = value.trim().replace(/^#/, "")
  const full = hex.length === 3 ? hex.replace(/./g, (c) => c + c) : hex
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16)
  }
}

function toHex({ r, g, b }: Rgb): string {
  const channel = (n: number) => Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, "0")
  return `#${channel(r)}${channel(g)}${channel(b)}`
}

/**
 * `color-mix(in srgb, a <weight>%, b)` for `#rgb` / `#rrggbb` inputs only.
 * Any other colour syntax returns `a` unchanged, while CSS color-mix() would
 * still mix it — for such a brand the pre-fill differs from what the games
 * render and Save persists it. Extend `parseHex` before onboarding a brand
 * whose palette is not hex.
 */
function mix(a: string | undefined, weight: number, b: string): string | undefined {
  const from = parseHex(a)
  const to = parseHex(b)
  if (!from || !to) return a
  const w = weight / 100
  return toHex({
    r: from.r * w + to.r * (1 - w),
    g: from.g * w + to.g * (1 - w),
    b: from.b * w + to.b * (1 - w)
  })
}

const WHITE = "#ffffff"
const BLACK = "#000000"

/**
 * What the games derive for this tenant while no override is stored, expressed
 * as overrides. Saving with the toggle on persists all of them as explicit
 * overrides: the games then stop following later `primary` / `secondary` edits
 * until the toggle is switched off again.
 */
export const deriveOriginalsDefaults = (colors: TenantColorsData): Required<Omit<TenantOriginalsColorsData, "gradient">> & {
  gradient: string[]
} => {
  const surface = colors.secondary
  const accent = colors.primary
  // --originals-src-text: color-mix(in srgb, white 92%, accent)
  const text = mix(WHITE, 92, accent) ?? WHITE

  return {
    surface,
    accent,
    text,
    // --originals-src-text-muted: color-mix(in srgb, <derived text> 52%, surface).
    // Anchored on the derived text, not on white: the CSS chains the two derivations.
    textMuted: mix(text, 52, surface) ?? text,
    // --originals-src-border: color-mix(in srgb, accent 30%, surface)
    border: mix(accent, 30, surface) ?? accent,
    // --originals-src-card: color-mix(in srgb, surface 72%, black)
    card: mix(surface, 72, BLACK) ?? surface,
    // --originals-src-button-text: var(--button-primary-text) — ThemeStyle emits
    // contentButtonTextColor into that variable.
    buttonText: colors.contentButtonTextColor,
    // --originals-src-gradient: var(--color-gradient-main) — ThemeStyle emits
    // `gradient.main` there. Copied so editing a stop never aliases the site
    // gradient array held in the same Formik state.
    gradient: [...(colors.gradient?.main ?? [])]
  }
}
